import React from 'react';
import styled from 'styled-components';
import { Users, Ban, Unlock, Search, Eye, Edit, X, Plus, Coins, History, Minus, Trash2, ExternalLink } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import { formatErrorDetail } from '../utils/errorFormat';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const SearchBox = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  max-width: 400px;
  width: 100%;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
`;

const TableContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${props => props.theme.colors.background};
`;

const Th = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
`;

const Badge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.bg || props.theme.colors.background};
  color: ${props => props.color || props.theme.colors.text};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  background: ${props => props.bg || props.theme.colors.primary};
  color: white;
  &:hover {
    opacity: 0.9;
  }
`;

const LoadingState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
  max-width: 1000px;
  width: 95%;
  max-height: 95vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text};
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.textSecondary};
  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.textSecondary};
`;

const InfoValue = styled.span`
  color: ${props => props.theme.colors.text};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  margin-top: ${props => props.theme.spacing.xs};
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  margin-top: ${props => props.theme.spacing.xs};
  min-height: 80px;
  resize: vertical;
`;

const Select = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  margin-top: ${props => props.theme.spacing.xs};
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-top: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
`;

const TabsContainer = styled.div`
  margin-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border};
  padding-top: ${props => props.theme.spacing.xl};
`;

const Tabs = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Tab = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  cursor: pointer;
  font-weight: ${props => props.active ? 600 : 400};
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const TabContent = styled.div`
  margin-top: ${props => props.theme.spacing.md};
`;

function UsersPage() {
  const [search, setSearch] = React.useState('');
  const [banned, setBanned] = React.useState(null);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showBalanceModal, setShowBalanceModal] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('info');
  const [editForm, setEditForm] = React.useState({});
  const [balanceAmount, setBalanceAmount] = React.useState('');
  const [balanceReason, setBalanceReason] = React.useState('');
  const [balanceOperation, setBalanceOperation] = React.useState('add'); // 'add' or 'subtract'
  const [selectedJob, setSelectedJob] = React.useState(null);
  const [expandedJobs, setExpandedJobs] = React.useState(new Set());
  
  const cancelJobMutation = useMutation(
    ({ jobId, reason }) => adminAPI.cancelJob(jobId, reason),
    {
      onSuccess: () => {
        toast.success('Задача отменена');
        queryClient.invalidateQueries(['userJobs']);
      },
      onError: (err) => {
        toast.error('Не удалось отменить задачу');
        console.error('Cancel job error:', err);
      }
    }
  );

  const retryJobMutation = useMutation(
    (jobId) => adminAPI.retryJob(jobId),
    {
      onSuccess: () => {
        toast.success('Задача поставлена в очередь повторно');
        queryClient.invalidateQueries(['userJobs']);
      },
      onError: (err) => {
        toast.error('Не удалось повторить задачу');
        console.error('Retry job error:', err);
      }
    }
  );
  
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery(
    ['users', search, banned],
    () => adminAPI.getUsers({ params: { search, banned } }),
    {
      keepPreviousData: true,
      onError: (err) => {
        console.error('Users error:', err);
      }
    }
  );

  const { data: userDetails } = useQuery(
    ['userDetails', selectedUser],
    () => {
      // Find the user from the current list to get tg_id
      const user = data?.data?.users.find(u => u.id === selectedUser);
      return adminAPI.getUserDetails(user?.tg_id || selectedUser);
    },
    {
      enabled: !!selectedUser && (showModal || showEditModal),
      onError: (err) => {
        toast.error('Не удалось загрузить детали пользователя');
        console.error('User details error:', err);
      },
      onSuccess: (data) => {
        if (showEditModal && data?.data) {
          setEditForm({
            username: data.data.username || '',
            first_name: data.data.first_name || '',
            last_name: data.data.last_name || '',
            lang: data.data.lang || 'ru',
            notes: data.data.notes || ''
          });
        }
      }
    }
  );

  const { data: userJobs } = useQuery(
    ['userJobs', selectedUser],
    () => {
      const user = data?.data?.users.find(u => u.id === selectedUser);
      return adminAPI.getUserJobs(user?.tg_id || selectedUser, { params: { limit: 50 } });
    },
    {
      enabled: !!selectedUser && showModal, // Load even when not on jobs tab for statistics
      onError: (err) => {
        console.error('User jobs error:', err);
      }
    }
  );

  const { data: userPayments } = useQuery(
    ['userPayments', selectedUser],
    () => {
      const user = data?.data?.users.find(u => u.id === selectedUser);
      return adminAPI.getUserPayments(user?.tg_id || selectedUser, { params: { limit: 50 } });
    },
    {
      enabled: !!selectedUser && showModal, // Load even when not on payments tab for statistics
      onError: (err) => {
        console.error('User payments error:', err);
      }
    }
  );

  const { data: userSubscriptions } = useQuery(
    ['userSubscriptions', selectedUser],
    () => {
      const user = data?.data?.users.find(u => u.id === selectedUser);
      return adminAPI.getUserSubscriptions(user?.tg_id || selectedUser);
    },
    {
      enabled: !!selectedUser && showModal,
      onError: (err) => {
        console.error('User subscriptions error:', err);
      }
    }
  );

  const { data: userPackages } = useQuery(
    ['userPackages', selectedUser],
    () => {
      const user = data?.data?.users.find(u => u.id === selectedUser);
      return adminAPI.getUserPackages(user?.tg_id || selectedUser);
    },
    {
      enabled: !!selectedUser && showModal,
      onError: (err) => {
        console.error('User packages error:', err);
      }
    }
  );

  const banMutation = useMutation(
    ({ userId, reason }) => adminAPI.banUser(userId, reason),
    {
      onSuccess: () => {
        toast.success('Пользователь забанен');
        queryClient.invalidateQueries(['users']);
        setShowModal(false);
      },
      onError: (err) => {
        toast.error('Не удалось забанить пользователя');
        console.error('Ban error:', err);
      }
    }
  );

  const unbanMutation = useMutation(
    (userId) => adminAPI.unbanUser(userId),
    {
      onSuccess: () => {
        toast.success('Пользователь разбанен');
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries(['userDetails']);
        setShowModal(false);
      },
      onError: (err) => {
        toast.error('Не удалось разбанить пользователя');
        console.error('Unban error:', err);
      }
    }
  );

  const deleteUserMutation = useMutation(
    (userId) => adminAPI.deleteUser(userId),
    {
      onSuccess: (data, userId) => {
        toast.success('Пользователь удален');
        queryClient.invalidateQueries(['users']);
        // Close modal if it's open for the deleted user
        if (showModal && userDetails?.data?.tg_id === userId) {
          setShowModal(false);
        }
      },
      onError: (err) => {
        toast.error('Не удалось удалить пользователя');
        console.error('Delete error:', err);
      }
    }
  );

  const updateUserMutation = useMutation(
    ({ userId, userData }) => adminAPI.updateUser(userId, userData),
    {
      onSuccess: () => {
        toast.success('Пользователь обновлен');
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries(['userDetails']);
        setShowEditModal(false);
        setEditForm({});
      },
      onError: (err) => {
        toast.error('Не удалось обновить пользователя');
        console.error('Update error:', err);
      }
    }
  );

  const adjustBalanceMutation = useMutation(
    async ({ userId, amount, reason }) => {
      console.log('🔵 [MUTATION] adjustBalanceMutation.mutate called with:', { userId, amount, reason });
      try {
        const result = await adminAPI.adjustBalance(userId, amount, reason);
        console.log('🟢 [MUTATION] adjustBalance API call successful:', result);
        return result;
      } catch (error) {
        console.error('🔴 [MUTATION] adjustBalance API call failed:', error);
        throw error;
      }
    },
    {
      onMutate: (variables) => {
        console.log('🟡 [MUTATION] onMutate called:', variables);
      },
      onSuccess: (data, variables) => {
        console.log('✅ [MUTATION] onSuccess called:', { data, variables });
        const operationText = variables.amount < 0 ? 'списано' : 'добавлено';
        toast.success(`Баланс обновлен: ${Math.abs(variables.amount)} кредитов ${operationText}`);
        queryClient.invalidateQueries(['userDetails']);
        queryClient.invalidateQueries(['users']);
        setShowBalanceModal(false);
        setBalanceAmount('');
        setBalanceReason('');
        setBalanceOperation('add');
      },
      onError: (err, variables, context) => {
        console.error('❌ [MUTATION] onError called:', { err, variables, context });
        console.error('Error response:', err?.response);
        console.error('Error data:', err?.response?.data);
        const errorMessage = formatErrorDetail(err, 'Неизвестная ошибка');
        console.error('Error message:', errorMessage);
        toast.error(`Не удалось обновить баланс: ${errorMessage}`);
      }
    }
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ru-RU');
  };

  const handleViewUser = (user) => {
    setSelectedUser(user.id);
    setShowModal(true);
  };

  const handleBanUser = () => {
    const reason = prompt('Причина блокировки:');
    if (reason && userDetails?.data) {
      banMutation.mutate({ userId: userDetails.data.tg_id, reason });
    }
  };

  const handleUnbanUser = () => {
    if (userDetails?.data) {
      unbanMutation.mutate(userDetails.data.tg_id);
    }
  };

  const handleDeleteUser = (user) => {
    const username = user.username || user.first_name || `ID ${user.tg_id}`;
    if (window.confirm(`Вы уверены, что хотите удалить пользователя ${username}? Это действие необратимо!`)) {
      deleteUserMutation.mutate(user.tg_id);
    }
  };

  const handleEditUser = () => {
    setShowEditModal(true);
    setActiveTab('info');
  };

  const handleSaveEdit = () => {
    if (userDetails?.data) {
      updateUserMutation.mutate({
        userId: userDetails.data.tg_id,
        userData: editForm
      });
    }
  };

  const handleAddBalance = () => {
    console.log('handleAddBalance called', { userDetails: userDetails?.data });
    if (userDetails?.data) {
      setShowBalanceModal(true);
      setBalanceAmount('');
      setBalanceReason('');
      setBalanceOperation('add');
      console.log('Balance modal opened');
    } else {
      console.error('Cannot open balance modal - userDetails is missing');
      toast.error('Не удалось загрузить данные пользователя');
    }
  };

  const handleSaveBalance = () => {
    console.log('handleSaveBalance called', { userDetails: userDetails?.data, balanceAmount, balanceOperation });
    if (!userDetails?.data) {
      toast.error('Данные пользователя не загружены');
      return;
    }
    if (!balanceAmount || (typeof balanceAmount === 'string' && balanceAmount.trim() === '')) {
      toast.error('Введите количество кредитов');
      return;
    }
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Количество кредитов должно быть положительным числом');
      return;
    }
    
    // Apply operation: subtract if operation is 'subtract'
    const finalAmount = balanceOperation === 'subtract' ? -Math.abs(amount) : Math.abs(amount);
    
    // Check if balance would go negative
    const currentBalance = userDetails.data.balance?.credits_available || 0;
    if (balanceOperation === 'subtract' && currentBalance + finalAmount < 0) {
      toast.error(`Недостаточно средств. Текущий баланс: ${currentBalance} кредитов`);
      return;
    }
    
    const defaultReason = balanceOperation === 'subtract' 
      ? 'Административное списание' 
      : 'Административное пополнение';
    
    console.log('Calling adjustBalanceMutation with:', {
      userId: userDetails.data.tg_id,
      amount: finalAmount,
      reason: balanceReason || defaultReason
    });
    adjustBalanceMutation.mutate({
      userId: userDetails.data.tg_id,
      amount: finalAmount,
      reason: balanceReason || defaultReason
    });
  };

  return (
    <Container>
      <Header>
        <Title>
          <Users size={32} />
          Пользователи
        </Title>
        <SearchBox>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
            <SearchInput
              placeholder="Поиск по username..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: '40px' }}
            />
          </div>
        </SearchBox>
      </Header>

      {isLoading ? (
        <LoadingState>Загрузка пользователей...</LoadingState>
      ) : error ? (
        <EmptyState>Ошибка загрузки данных</EmptyState>
      ) : !data || !data.data || !data.data.users || data.data.users.length === 0 ? (
        <EmptyState>Пользователи не найдены</EmptyState>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <Th>ID</Th>
                  <Th>Telegram ID</Th>
                  <Th>Username</Th>
                  <Th>Язык</Th>
                  <Th>Статус</Th>
                  <Th>Дата регистрации</Th>
                  <Th>Действия</Th>
                </tr>
              </TableHeader>
              <tbody>
                {data.data.users.map((user) => (
                  <tr key={user.id}>
                    <Td>{user.id}</Td>
                    <Td>{user.tg_id}</Td>
                    <Td>
                      {(() => {
                        // Priority: username (if not user_*) > first_name (if not Пользователь) > default
                        if (user.username && !user.username.startsWith('user_')) {
                          return <Badge bg="#e0e7ff" color="#4f46e5">@{user.username}</Badge>;
                        }
                        if (user.first_name && user.first_name !== 'Пользователь') {
                          return <Badge bg="#f0f9ff" color="#0284c7">{user.first_name}</Badge>;
                        }
                        if (user.first_name) {
                          return <Badge bg="#f0f9ff" color="#0284c7">{user.first_name}</Badge>;
                        }
                        return <Badge bg="#f3f4f6" color="#6b7280">Без имени</Badge>;
                      })()}
                    </Td>
                    <Td>{user.lang || 'ru'}</Td>
                    <Td>
                      {user.ban_flag ? (
                        <Badge bg="#fee2e2" color="#dc2626">Забанен</Badge>
                      ) : (
                        <Badge bg="#d1fae5" color="#059669">Активен</Badge>
                      )}
                    </Td>
                    <Td>{formatDate(user.created_at)}</Td>
                    <Td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button bg="#0284c7" style={{ fontSize: '12px' }} onClick={() => handleViewUser(user)}>
                          <Eye size={14} style={{ verticalAlign: 'middle' }} />
                        </Button>
                        <Button 
                          bg="#991b1b" 
                          style={{ fontSize: '12px' }} 
                          onClick={() => handleDeleteUser(user)}
                          disabled={deleteUserMutation.isLoading}
                        >
                          <Trash2 size={14} style={{ verticalAlign: 'middle' }} />
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <div style={{ marginTop: '16px', textAlign: 'center', color: '#6b7280' }}>
            Всего: {data.data.total || 0}
          </div>
        </>
      )}

      {showModal && userDetails?.data && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Информация о пользователе</ModalTitle>
              <ModalCloseButton onClick={() => setShowModal(false)}>
                <X size={24} />
              </ModalCloseButton>
            </ModalHeader>

            <TabsContainer>
              <Tabs>
                <Tab active={activeTab === 'info'} onClick={() => setActiveTab('info')}>
                  Информация
                </Tab>
                <Tab active={activeTab === 'jobs'} onClick={() => setActiveTab('jobs')}>
                  Задачи ({userJobs?.data?.total || 0})
                </Tab>
                <Tab active={activeTab === 'payments'} onClick={() => setActiveTab('payments')}>
                  Платежи ({userPayments?.data?.total || 0})
                </Tab>
                <Tab active={activeTab === 'billing'} onClick={() => setActiveTab('billing')}>
                  Подписки и пакеты
                </Tab>
              </Tabs>

              <TabContent>
                {activeTab === 'info' && (
                  <div>
                    <InfoRow>
                      <InfoLabel>ID:</InfoLabel>
                      <InfoValue>{userDetails.data.id}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Telegram ID:</InfoLabel>
                      <InfoValue>{userDetails.data.tg_id}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Username:</InfoLabel>
                      <InfoValue>@{userDetails.data.username || 'N/A'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Имя:</InfoLabel>
                      <InfoValue>{userDetails.data.first_name || 'N/A'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Язык:</InfoLabel>
                      <InfoValue>{userDetails.data.lang || 'ru'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Статус:</InfoLabel>
                      <InfoValue>
                        {userDetails.data.ban_flag ? (
                          <Badge bg="#fee2e2" color="#dc2626">Забанен</Badge>
                        ) : (
                          <Badge bg="#d1fae5" color="#059669">Активен</Badge>
                        )}
                      </InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Дата регистрации:</InfoLabel>
                      <InfoValue>{formatDate(userDetails.data.created_at)}</InfoValue>
                    </InfoRow>
                    {userDetails.data.balance && (
                      <>
                        <InfoRow>
                          <InfoLabel>Кредитов всего:</InfoLabel>
                          <InfoValue>{userDetails.data.balance.credits_total}</InfoValue>
                        </InfoRow>
                        <InfoRow>
                          <InfoLabel>Кредитов доступно:</InfoLabel>
                          <InfoValue>{userDetails.data.balance.credits_available}</InfoValue>
                        </InfoRow>
                      </>
                    )}
                    {userDetails.data.notes && (
                      <InfoRow>
                        <InfoLabel>Примечания:</InfoLabel>
                        <InfoValue>{userDetails.data.notes}</InfoValue>
                      </InfoRow>
                    )}
                    <InfoRow style={{ marginTop: '16px', paddingTop: '16px', borderTop: '2px solid #e5e7eb' }}>
                      <InfoLabel style={{ fontSize: '14px', fontWeight: 'bold' }}>Статистика:</InfoLabel>
                      <InfoValue></InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Всего задач:</InfoLabel>
                      <InfoValue>{userJobs?.data?.total || 0}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Всего платежей:</InfoLabel>
                      <InfoValue>{userPayments?.data?.total || 0}</InfoValue>
                    </InfoRow>
                    {userJobs?.data?.jobs && userJobs.data.jobs.length > 0 && (
                      <InfoRow>
                        <InfoLabel>Потрачено кредитов:</InfoLabel>
                        <InfoValue style={{ color: '#dc2626' }}>
                          {userJobs.data.jobs.reduce((sum, job) => sum + (parseFloat(job.cost_credits) || 0), 0).toFixed(2)} ⭐
                        </InfoValue>
                      </InfoRow>
                    )}
                    {userPayments?.data?.payments && userPayments.data.payments.length > 0 && (
                      <>
                        <InfoRow>
                          <InfoLabel>Пополнено (RUB):</InfoLabel>
                          <InfoValue style={{ color: '#059669' }}>
                            {userPayments.data.payments
                              .filter(p => ['paid', 'succeeded', 'completed'].includes(p.status))
                              .reduce((sum, p) => sum + (parseFloat(p.amount_rub) || 0), 0).toFixed(2)}₽
                          </InfoValue>
                        </InfoRow>
                        <InfoRow>
                          <InfoLabel>Получено кредитов:</InfoLabel>
                          <InfoValue style={{ color: '#059669' }}>
                            {userPayments.data.payments
                              .filter(p => ['paid', 'succeeded', 'completed'].includes(p.status))
                              .reduce((sum, p) => sum + (parseFloat(p.credits) || 0), 0)} ⭐
                          </InfoValue>
                        </InfoRow>
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'jobs' && (
                  <div>
                    {userJobs?.data?.jobs && userJobs.data.jobs.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHeader>
                            <tr>
                              <Th style={{ width: '60px' }}>▼</Th>
                              <Th>ID</Th>
                              <Th>Тип</Th>
                              <Th>Статус</Th>
                              <Th>Prompt</Th>
                              <Th>Стоимость</Th>
                              <Th>Результат</Th>
                              <Th>Дата</Th>
                            </tr>
                          </TableHeader>
                          <tbody>
                            {userJobs.data.jobs.map((job) => {
                              const isExpanded = expandedJobs.has(job.id);
                              return (
                                <React.Fragment key={job.id}>
                                  <tr style={{ cursor: 'pointer' }} onClick={() => {
                                    const newExpanded = new Set(expandedJobs);
                                    if (newExpanded.has(job.id)) {
                                      newExpanded.delete(job.id);
                                    } else {
                                      newExpanded.add(job.id);
                                    }
                                    setExpandedJobs(newExpanded);
                                  }}>
                                    <Td style={{ textAlign: 'center' }}>
                                      {isExpanded ? '▼' : '▶'}
                                    </Td>
                                    <Td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{String(job.id).slice(0, 8)}</Td>
                                    <Td>{job.type}</Td>
                                    <Td>
                                      <Badge 
                                        bg={
                                          job.status === 'completed' || job.status === 'succeeded' ? '#d1fae5' : 
                                          job.status === 'failed' || job.status === 'canceled' ? '#fee2e2' :
                                          job.status === 'running' || job.status === 'queued' ? '#fef3c7' : '#e5e7eb'
                                        } 
                                        color={
                                          job.status === 'completed' || job.status === 'succeeded' ? '#059669' : 
                                          job.status === 'failed' || job.status === 'canceled' ? '#dc2626' :
                                          job.status === 'running' || job.status === 'queued' ? '#d97706' : '#6b7280'
                                        }
                                      >
                                        {job.status}
                                      </Badge>
                                    </Td>
                                    <Td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {job.prompt ? (job.prompt.length > 30 ? job.prompt.substring(0, 30) + '...' : job.prompt) : '-'}
                                    </Td>
                                    <Td>{job.cost_credits || 0} ⭐</Td>
                                    <Td>
                                      {job.result_url ? (
                                        // Для текстовых задач result_url содержит сам текст, а не URL
                                        job.type === 'text' && !job.result_url.match(/^https?:\/\//) ? (
                                          <span style={{ color: '#059669', fontSize: '12px' }}>Текст</span>
                                        ) : (
                                          <a href={job.result_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#0284c7', textDecoration: 'none' }}>
                                            <ExternalLink size={14} style={{ verticalAlign: 'middle' }} /> Открыть
                                          </a>
                                        )
                                      ) : job.status === 'failed' ? (
                                        <span style={{ color: '#dc2626', fontSize: '12px' }}>Ошибка</span>
                                      ) : '-'}
                                    </Td>
                                    <Td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(job.created_at)}</Td>
                                  </tr>
                                  {isExpanded && (
                                    <tr style={{ background: '#f9fafb' }}>
                                      <Td colSpan="8" style={{ padding: '20px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                          <div>
                                            <strong>Prompt:</strong>
                                            <div style={{ marginTop: '8px', padding: '8px', background: '#fff', borderRadius: '8px', maxHeight: '100px', overflow: 'auto' }}>
                                              {job.prompt || '-'}
                                            </div>
                                          </div>
                                          <div>
                                            <strong>Negative Prompt:</strong>
                                            <div style={{ marginTop: '8px', padding: '8px', background: '#fff', borderRadius: '8px', maxHeight: '100px', overflow: 'auto' }}>
                                              {job.negative || '-'}
                                            </div>
                                          </div>
                                          {job.error && (
                                            <div style={{ gridColumn: '1 / -1' }}>
                                              <strong style={{ color: '#dc2626' }}>Ошибка:</strong>
                                              <div style={{ marginTop: '8px', padding: '8px', background: '#fee2e2', borderRadius: '8px', color: '#dc2626' }}>
                                                {job.error}
                                              </div>
                                            </div>
                                          )}
                                          {job.result_url && (
                                            <div style={{ gridColumn: '1 / -1' }}>
                                              <strong>Результат:</strong>
                                              <div style={{ marginTop: '8px' }}>
                                                {/* Проверяем, является ли result_url URL или текстом (для текстовых задач) */}
                                                {job.type === 'text' && !job.result_url.match(/^https?:\/\//) ? (
                                                  // Для текстовых задач result_url содержит сам текст
                                                  <div style={{ 
                                                    padding: '12px', 
                                                    background: '#fff', 
                                                    borderRadius: '8px', 
                                                    maxHeight: '400px', 
                                                    overflow: 'auto',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    lineHeight: '1.6',
                                                    fontSize: '14px',
                                                    border: '1px solid #e5e7eb'
                                                  }}>
                                                    {job.result_url}
                                                  </div>
                                                ) : (
                                                  // Для изображений и других - показываем ссылку
                                                  <>
                                                    <a href={job.result_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0284c7', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                      <ExternalLink size={16} /> {job.result_url}
                                                    </a>
                                                    {job.result_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                                                      <div style={{ marginTop: '8px' }}>
                                                        <img src={job.result_url} alt="Result" style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px' }} />
                                                      </div>
                                                    )}
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                          <div>
                                            <strong>Создано:</strong> {formatDate(job.created_at)}
                                          </div>
                                          {job.started_at && (
                                            <div>
                                              <strong>Начато:</strong> {formatDate(job.started_at)}
                                            </div>
                                          )}
                                          {job.finished_at && (
                                            <div>
                                              <strong>Завершено:</strong> {formatDate(job.finished_at)}
                                            </div>
                                          )}
                                          <div>
                                            <strong>Стоимость:</strong> {job.cost_credits || 0} кредитов
                                          </div>
                                          <div>
                                            <strong>Provider ID:</strong> {job.provider_id || '-'}
                                          </div>
                                          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px', marginTop: '16px' }}>
                                            {(job.status === 'running' || job.status === 'queued' || job.status === 'pending') && (
                                              <Button
                                                bg="#ef4444"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const reason = prompt('Причина отмены:', 'Отменено администратором');
                                                  if (reason) {
                                                    cancelJobMutation.mutate({ jobId: job.id, reason });
                                                  }
                                                }}
                                                disabled={cancelJobMutation.isLoading}
                                                style={{ fontSize: '12px' }}
                                              >
                                                <Trash2 size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                                Отменить
                                              </Button>
                                            )}
                                            {(job.status === 'failed' || job.status === 'canceled') && (
                                              <Button
                                                bg="#0284c7"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (window.confirm('Повторить эту задачу?')) {
                                                    retryJobMutation.mutate(job.id);
                                                  }
                                                }}
                                                disabled={retryJobMutation.isLoading}
                                                style={{ fontSize: '12px' }}
                                              >
                                                <History size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                                Повторить
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      </Td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <EmptyState>Задачи не найдены</EmptyState>
                    )}
                  </div>
                )}

                {activeTab === 'payments' && (
                  <div>
                    {userPayments?.data?.payments && userPayments.data.payments.length > 0 ? (
                      <TableContainer>
                        <Table>
                          <TableHeader>
                            <tr>
                              <Th>ID</Th>
                              <Th>Провайдер</Th>
                              <Th>Сумма</Th>
                              <Th>Кредиты</Th>
                              <Th>Статус</Th>
                              <Th>TX ID</Th>
                              <Th>Дата</Th>
                            </tr>
                          </TableHeader>
                          <tbody>
                            {userPayments.data.payments.map((payment) => (
                              <tr key={payment.id}>
                                <Td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{payment.id}</Td>
                                <Td>{payment.provider || '-'}</Td>
                                <Td>{payment.amount_rub}₽ {payment.currency && payment.currency !== 'RUB' ? `(${payment.currency})` : ''}</Td>
                                <Td>{payment.credits || 0} ⭐</Td>
                                <Td>
                                  <Badge 
                                    bg={
                                      payment.status === 'completed' ? '#d1fae5' : 
                                      payment.status === 'failed' ? '#fee2e2' :
                                      payment.status === 'pending' ? '#fef3c7' : '#e5e7eb'
                                    } 
                                    color={
                                      payment.status === 'completed' ? '#059669' : 
                                      payment.status === 'failed' ? '#dc2626' :
                                      payment.status === 'pending' ? '#d97706' : '#6b7280'
                                    }
                                  >
                                    {payment.status}
                                  </Badge>
                                </Td>
                                <Td style={{ fontFamily: 'monospace', fontSize: '11px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {payment.tx_id || '-'}
                                </Td>
                                <Td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(payment.created_at)}</Td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <EmptyState>Платежи не найдены</EmptyState>
                    )}
                  </div>
                )}

                {activeTab === 'billing' && (
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '16px', color: '#1f2937' }}>
                        Подписки ({userSubscriptions?.data?.total || 0})
                      </h3>
                      {userSubscriptions?.data?.subscriptions && userSubscriptions.data.subscriptions.length > 0 ? (
                        <TableContainer>
                          <Table>
                            <TableHeader>
                              <tr>
                                <Th>ID</Th>
                                <Th>Тариф</Th>
                                <Th>Статус</Th>
                                <Th>Автопродление</Th>
                                <Th>Начало</Th>
                                <Th>Окончание</Th>
                              </tr>
                            </TableHeader>
                            <tbody>
                              {userSubscriptions.data.subscriptions.map((sub) => (
                                <tr key={sub.id}>
                                  <Td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{sub.id}</Td>
                                  <Td>
                                    <div>
                                      <div style={{ fontWeight: '600' }}>{sub.plan_name || sub.plan_key || 'N/A'}</div>
                                      {sub.plan_key && (
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{sub.plan_key}</div>
                                      )}
                                    </div>
                                  </Td>
                                  <Td>
                                    <Badge 
                                      bg={
                                        sub.status === 'active' ? '#d1fae5' : 
                                        sub.status === 'canceled' ? '#fee2e2' :
                                        sub.status === 'expired' ? '#f3f4f6' : '#fef3c7'
                                      } 
                                      color={
                                        sub.status === 'active' ? '#059669' : 
                                        sub.status === 'canceled' ? '#dc2626' :
                                        sub.status === 'expired' ? '#6b7280' : '#d97706'
                                      }
                                    >
                                      {sub.status}
                                    </Badge>
                                  </Td>
                                  <Td>
                                    {sub.auto_renew ? (
                                      <Badge bg="#d1fae5" color="#059669">Да</Badge>
                                    ) : (
                                      <Badge bg="#f3f4f6" color="#6b7280">Нет</Badge>
                                    )}
                                  </Td>
                                  <Td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(sub.started_at)}</Td>
                                  <Td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(sub.expires_at) || 'Без ограничений'}</Td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <EmptyState>Подписки не найдены</EmptyState>
                      )}
                    </div>

                    <div style={{ marginTop: '32px' }}>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '16px', color: '#1f2937' }}>
                        Активные пакеты ({userPackages?.data?.total_allowances || 0})
                      </h3>
                      {userPackages?.data?.allowances && userPackages.data.allowances.length > 0 ? (
                        <TableContainer>
                          <Table>
                            <TableHeader>
                              <tr>
                                <Th>Категория</Th>
                                <Th>Осталось запросов</Th>
                                <Th>Осталось генераций</Th>
                                <Th>Обновлено</Th>
                              </tr>
                            </TableHeader>
                            <tbody>
                              {userPackages.data.allowances.map((allowance, idx) => (
                                <tr key={idx}>
                                  <Td>
                                    <Badge bg="#e0e7ff" color="#4f46e5">{allowance.category}</Badge>
                                  </Td>
                                  <Td>{allowance.remaining_requests || 0}</Td>
                                  <Td>{allowance.remaining_generations || 0}</Td>
                                  <Td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(allowance.updated_at)}</Td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <EmptyState>Активные пакеты не найдены</EmptyState>
                      )}
                    </div>

                    <div style={{ marginTop: '32px' }}>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '16px', color: '#1f2937' }}>
                        История покупок пакетов ({userPackages?.data?.total_purchases || 0})
                      </h3>
                      {userPackages?.data?.purchase_history && userPackages.data.purchase_history.length > 0 ? (
                        <TableContainer>
                          <Table>
                            <TableHeader>
                              <tr>
                                <Th>ID</Th>
                                <Th>Пакет</Th>
                                <Th>Опция</Th>
                                <Th>Сумма</Th>
                                <Th>Статус</Th>
                                <Th>Дата</Th>
                              </tr>
                            </TableHeader>
                            <tbody>
                              {userPackages.data.purchase_history.map((purchase) => (
                                <tr key={purchase.id}>
                                  <Td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{purchase.id}</Td>
                                  <Td>
                                    <div>
                                      <div style={{ fontWeight: '600' }}>{purchase.package_name || purchase.package_key || 'N/A'}</div>
                                      {purchase.package_category && (
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{purchase.package_category}</div>
                                      )}
                                    </div>
                                  </Td>
                                  <Td>
                                    {purchase.option_label && (
                                      <div>
                                        <div>{purchase.option_label}</div>
                                        {(purchase.option_requests || purchase.option_generations) && (
                                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                            {purchase.option_requests ? `${purchase.option_requests} запросов` : ''}
                                            {purchase.option_requests && purchase.option_generations ? ', ' : ''}
                                            {purchase.option_generations ? `${purchase.option_generations} генераций` : ''}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Td>
                                  <Td>{purchase.amount_rub}₽</Td>
                                  <Td>
                                    <Badge 
                                      bg={
                                        purchase.status === 'completed' || purchase.status === 'paid' ? '#d1fae5' : 
                                        purchase.status === 'failed' ? '#fee2e2' :
                                        purchase.status === 'pending' ? '#fef3c7' : '#e5e7eb'
                                      } 
                                      color={
                                        purchase.status === 'completed' || purchase.status === 'paid' ? '#059669' : 
                                        purchase.status === 'failed' ? '#dc2626' :
                                        purchase.status === 'pending' ? '#d97706' : '#6b7280'
                                      }
                                    >
                                      {purchase.status}
                                    </Badge>
                                  </Td>
                                  <Td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(purchase.created_at)}</Td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <EmptyState>История покупок пуста</EmptyState>
                      )}
                    </div>
                  </div>
                )}
              </TabContent>
            </TabsContainer>

            <ActionButtons>
              <Button bg="#0284c7" onClick={handleEditUser}>
                <Edit size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Редактировать
              </Button>
              <Button bg="#10b981" onClick={handleAddBalance}>
                <Coins size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Управление балансом
              </Button>
              {userDetails.data.ban_flag ? (
                <Button bg="#059669" onClick={handleUnbanUser}>
                  <Unlock size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Разблокировать
                </Button>
              ) : (
                <Button bg="#dc2626" onClick={handleBanUser}>
                  <Ban size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Заблокировать
                </Button>
              )}
            </ActionButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Edit User Modal */}
      {showEditModal && userDetails?.data && (
        <ModalOverlay onClick={() => setShowEditModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Редактировать пользователя</ModalTitle>
              <ModalCloseButton onClick={() => setShowEditModal(false)}>
                <X size={24} />
              </ModalCloseButton>
            </ModalHeader>
            
            <div>
              <Label>Username:</Label>
              <Input
                type="text"
                value={editForm.username || ''}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                placeholder="username"
              />
              
              <Label>Имя:</Label>
              <Input
                type="text"
                value={editForm.first_name || ''}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                placeholder="Имя"
              />
              
              <Label>Фамилия:</Label>
              <Input
                type="text"
                value={editForm.last_name || ''}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                placeholder="Фамилия"
              />
              
              <Label>Язык:</Label>
              <Select
                value={editForm.lang || 'ru'}
                onChange={(e) => setEditForm({ ...editForm, lang: e.target.value })}
              >
                <option value="ru">Русский</option>
                <option value="en">English</option>
              </Select>
              
              <Label>Примечания:</Label>
              <TextArea
                value={editForm.notes || ''}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder="Примечания о пользователе"
              />
            </div>

            <ActionButtons>
              <Button bg="#10b981" onClick={handleSaveEdit} disabled={updateUserMutation.isLoading}>
                Сохранить
              </Button>
              <Button bg="#6b7280" onClick={() => setShowEditModal(false)}>
                Отмена
              </Button>
            </ActionButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Add Balance Modal */}
      {showBalanceModal && userDetails?.data && (
        <ModalOverlay onClick={() => {
          if (!adjustBalanceMutation.isLoading) {
            setShowBalanceModal(false);
          }
        }}>
          <ModalContent onClick={(e) => e.stopPropagation()} onKeyDown={(e) => {
            if (e.key === 'Escape' && !adjustBalanceMutation.isLoading) {
              setShowBalanceModal(false);
            }
          }}>
            <ModalHeader>
              <ModalTitle>Управление балансом</ModalTitle>
              <ModalCloseButton onClick={() => {
                console.log('Close button clicked');
                setShowBalanceModal(false);
              }}>
                <X size={24} />
              </ModalCloseButton>
            </ModalHeader>
            
            <div>
              {adjustBalanceMutation.isError && (
                <div style={{ padding: '12px', marginBottom: '16px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px' }}>
                  Ошибка: {formatErrorDetail(adjustBalanceMutation.error, 'Неизвестная ошибка')}
                </div>
              )}
              <InfoRow>
                <InfoLabel>Текущий баланс:</InfoLabel>
                <InfoValue style={{ fontSize: '18px', fontWeight: 'bold', color: balanceOperation === 'subtract' && parseFloat(balanceAmount || 0) > (userDetails.data.balance?.credits_available || 0) ? '#dc2626' : '#059669' }}>
                  {userDetails.data.balance?.credits_available || 0} кредитов
                </InfoValue>
              </InfoRow>
              
              <Label>Операция:</Label>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <Button
                  bg={balanceOperation === 'add' ? '#10b981' : '#e5e7eb'}
                  style={{ flex: 1, color: balanceOperation === 'add' ? '#fff' : '#6b7280' }}
                  onClick={() => setBalanceOperation('add')}
                  type="button"
                >
                  <Plus size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Добавить
                </Button>
                <Button
                  bg={balanceOperation === 'subtract' ? '#ef4444' : '#e5e7eb'}
                  style={{ flex: 1, color: balanceOperation === 'subtract' ? '#fff' : '#6b7280' }}
                  onClick={() => setBalanceOperation('subtract')}
                  type="button"
                >
                  <Minus size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Списать
                </Button>
              </div>
              
              <Label>Количество кредитов:</Label>
              <Input
                type="number"
                value={balanceAmount}
                onChange={(e) => setBalanceAmount(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveBalance();
                  }
                }}
                placeholder="100"
                min="1"
                step="1"
              />
              {balanceOperation === 'subtract' && balanceAmount && parseFloat(balanceAmount) > (userDetails.data.balance?.credits_available || 0) && (
                <div style={{ padding: '8px', marginTop: '8px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', fontSize: '14px' }}>
                  ⚠️ Недостаточно средств для списания
                </div>
              )}
              {balanceOperation === 'subtract' && balanceAmount && parseFloat(balanceAmount) <= (userDetails.data.balance?.credits_available || 0) && (
                <div style={{ padding: '8px', marginTop: '8px', background: '#d1fae5', color: '#059669', borderRadius: '8px', fontSize: '14px' }}>
                  Баланс после списания: {(userDetails.data.balance?.credits_available || 0) - parseFloat(balanceAmount || 0)} кредитов
                </div>
              )}
              
              <Label>Причина (необязательно):</Label>
              <TextArea
                value={balanceReason}
                onChange={(e) => setBalanceReason(e.target.value)}
                placeholder={balanceOperation === 'subtract' ? 'Административное списание' : 'Административное пополнение'}
              />
            </div>

            <ActionButtons>
              <Button 
                bg={balanceOperation === 'subtract' ? '#ef4444' : '#10b981'} 
                onClick={(e) => {
                  const isDisabled = adjustBalanceMutation.isLoading || !balanceAmount || balanceAmount === '';
                  console.log('🎯 [BUTTON] Add button clicked!', { 
                    event: e, 
                    isLoading: adjustBalanceMutation.isLoading, 
                    balanceAmount,
                    balanceAmountType: typeof balanceAmount,
                    balanceAmountLength: balanceAmount?.length,
                    isDisabled,
                    userDetails: userDetails?.data,
                    userDetailsTgId: userDetails?.data?.tg_id
                  });
                  if (isDisabled) {
                    console.warn('⚠️ [BUTTON] Button is disabled, click ignored');
                    return;
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🎯 [BUTTON] Calling handleSaveBalance...');
                  try {
                    handleSaveBalance();
                    console.log('🎯 [BUTTON] handleSaveBalance called successfully');
                  } catch (error) {
                    console.error('❌ [BUTTON] Error calling handleSaveBalance:', error);
                  }
                }} 
                disabled={adjustBalanceMutation.isLoading || !balanceAmount || balanceAmount === '' || balanceAmount.trim?.() === ''}
                type="button"
                style={{ 
                  cursor: (adjustBalanceMutation.isLoading || !balanceAmount || balanceAmount === '') ? 'not-allowed' : 'pointer',
                  opacity: (adjustBalanceMutation.isLoading || !balanceAmount || balanceAmount === '') ? 0.5 : 1
                }}
              >
                {adjustBalanceMutation.isLoading 
                  ? (balanceOperation === 'subtract' ? 'Списание...' : 'Добавление...') 
                  : (balanceOperation === 'subtract' ? 'Списать' : 'Добавить')}
              </Button>
              <Button 
                bg="#6b7280" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowBalanceModal(false);
                }}
                type="button"
              >
                Отмена
              </Button>
            </ActionButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default UsersPage;
